import React, { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, Modal, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import useAuth from '../../../../context/auth/useAuth.js'
import ClubBreadCrumbs from '../../../../components/breadcrumbs/ClubBreadCrumbs.js'
import { checkInOut, getChecksByMatch, getMatch } from '../../../../actions/ticketing/matchesActions.js'
import { getPassesByIds } from '../../../../actions/ticketing/passesActions.js'
import Loader from '../../../../components/design/Loader/Loader.js'
import { CustomDataTable } from '../../../../components/design/Datatables/CustomDataTable'
import TicketingSubMenu from '../../../../components/menus/TicketingSubMenu.js'
import * as XLSX from 'xlsx'

export default function Checks({ usertype, selectedMatch }) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()

    const { club } = useSelector(state => state.clubs)
    const { check, checks, loadingentry, match } = useSelector(state => state.matches)
    const { passes } = useSelector(state => state.pass)

    const getPassHolderData = (check) => {
        if (check?.entryType === 'Pass' && passes) {
            const pass = passes.find(p => p._id === check.entry._id)
            if (pass) {
                return {
                    name: `${pass.personData?.firstname || ''} ${pass.personData?.lastname || ''}`,
                    dni: pass.client?.dni || '',
                    sector: pass.sector?.name || ''
                }
            }
        }

        if (check?.entryType === 'InvitationUse') {
            const invitationPersonData = check?.entry?.invitation?.personData || {}
            return {
                name: invitationPersonData?.firstname ? `${invitationPersonData.firstname} ${invitationPersonData.lastname || ''}` : '',
                dni: invitationPersonData?.dni || '',
                sector: check?.entry?.invitation?.sector?.name || ''
            }
        }

        return {
            name: check?.entry?.personData?.firstname ? `${check.entry.personData.firstname} ${check.entry.personData.lastname || ''}` : '',
            dni: check?.entry?.personData?.dni || '',
            sector: check?.entry?.sector?.name || ''
        }
    }

    const exportToExcel = () => {
        if (!checks || checks.length === 0) return

        const dataToExport = checks.map(check => {
            const holderData = getPassHolderData(check)
            return {
                [t('matches.identifier')]: check?.entry?.identifier || '',
                [t('matches.name')]: holderData.name,
                [t('matches.dni')]: holderData.dni,
                [t('matches.sector')]: check?.entryType === 'Pass' || check?.entryType === 'InvitationUse' ? holderData.sector : '',
                [t('matches.contadorAccesos')]: check?.checkins?.length || 0,
                [t('matches.checkins')]: check?.checkins?.map(time => new Date(time).toLocaleString('es-ES', { 
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })).join(', ') || '',
                [t('matches.checkouts')]: check?.checkouts?.map(time => new Date(time).toLocaleString('es-ES', { 
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })).join(', ') || '',
                [t('matches.lastmove')]: check?.status === 1 ? t('matches.out') : t('matches.in')
            }
        })

        const ws = XLSX.utils.json_to_sheet(dataToExport)
        
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Registros')
        
        const fileName = `registros_${match?.host}_${match?.visitor}_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(wb, fileName)
    }

    useEffect(() => {
        if(selectedMatch){
            dispatch(getChecksByMatch(selectedMatch))
            dispatch(getMatch(selectedMatch))
        }
    }, [dispatch, clientauth, selectedMatch])

    useEffect(() => {
        if(checks) {
            // Filtrar solo los checks de tipo Pass y obtener sus IDs únicos
            const passIds = [...new Set(checks
                .filter(check => check.entryType === 'Pass')
                .map(check => check.entry._id))]
            
            if(passIds.length > 0) {
                dispatch(getPassesByIds(passIds))
            }
        }
    }, [dispatch, checks])

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
    }, [checks])

    useEffect(() => {
        if (check) {
            dispatch({type: 'MATCH_RESET_ENTRY'})
        }
    }, [check])

    const handleCheckInOut = (entryCode, currentStatus) => {
        const checkType = currentStatus === 1 ? 'outadmin' : 'inadmin'
        dispatch(checkInOut(entryCode, selectedMatch, checkType))
    }

	const columnsChecks = [
        {
            name: t('matches.identifier'),
            cell: check =>
                <div>
                    {check?.entry?.identifier}
                </div>,
            selector: (check) => check?.entry?.identifier,
            sortable: true,
        },{
            name: t('matches.name'),
            cell: check => {
                const holderData = getPassHolderData(check)
                return (
                    <div>
                        {holderData.name}
                    </div>
                )
            },
            selector: (check) => {
                const holderData = getPassHolderData(check)
                return holderData.name
            },
            sortable: true,
        },{
            name: t('matches.dni'),
            cell: check => {
                const holderData = getPassHolderData(check)
                return (
                    <div>
                        {holderData.dni}
                    </div>
                )
            },
            selector: (check) => {
                const holderData = getPassHolderData(check)
                return holderData.dni
            },
            sortable: true,
        },{
            name: t('matches.sector'),
            cell: check => {
                const holderData = getPassHolderData(check)
                return (
                    <div>
                        {check?.entryType === 'Pass' || check?.entryType === 'InvitationUse' ? holderData.sector : ''}
                    </div>
                )
            },
            selector: (check) => {
                const holderData = getPassHolderData(check)
                return check?.entryType === 'Pass' || check?.entryType === 'InvitationUse' ? holderData.sector : ''
            },
            sortable: true,
        },{
            name: t('matches.checkins'),
            cell: check => (
                <div>
                    {check?.checkins?.map(time => new Date(time).toLocaleString('es-ES', { 
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })).join(', ')}
                </div>
            ),
            selector: (check) => check.checkins,
            sortable: true,
        },
        {
            name: t('matches.checkouts'),
            cell: check => (
                <div>
                    {check?.checkouts?.map(time => new Date(time).toLocaleString('es-ES', { 
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })).join(', ')}
                </div>
            ),
            selector: (check) => check.checkouts,
            sortable: true,
        },{
            name: t('matches.contadorAccesos'),
            center: 'true',
            cell: check => (
                <div>
                    {check?.checkins?.length || 0}
                </div>
            ),
            selector: (check) => check?.checkins?.length || 0,
            sortable: true,
        },{
			name: t('matches.lastmove'),
            center: 'true',
            width: '120px',
			cell: check =>
				<div>
                    <select
                        value={check?.status}
                        onChange={(e) => handleCheckInOut(check?.entry?.identifier, Number(e.target.value))}
                        className="form-select form-select-sm"
                    >
                        <option value={0}>{t('matches.in')}</option>
                        <option value={1}>{t('matches.out')}</option>
                    </select>
                </div>,
			sortable: false,
		}
	]

    return (
        <>
            <Container className='cuerpo'>
                <ClubBreadCrumbs usertype={usertype} club={club} />
                <Row className="my-3">
                    <Col>
                        <TicketingSubMenu usertype={usertype} active="checks"/>
                    </Col>
                </Row>
                <Row className='my-3'>
                    <Col>
                        <Card>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    {match ?
                                        <h3 className="mb-0">{match?.host} - {match.visitor}</h3>
                                    :
                                        <h3 className="mb-0"></h3>
                                    }
                                    {checks && checks.length > 0 &&
                                        <Button variant="success" onClick={exportToExcel}>
                                            <i className="fas fa-file-excel me-2"></i>
                                            {t('global.exportToExcel')}
                                        </Button>
                                    }
                                </div>
                                {loadingentry ?
                                    <Loader/>
                                :
                                    checks ?
                                        checks?.length > 0 ?
                                            <CustomDataTable columns={columnsChecks} data={checks} exportable={false} printable={false} />
                                        :
                                            <div className='contenedor-vacio text-center'>
                                                <h4>{t('matches.thereisnotchecks')}</h4>
                                            </div>
                                    :
                                        <></>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    )
}